import React, { useState, useEffect, useRef } from 'react'
import { gql } from 'graphql-request'
import axios from 'axios'
import { authorizedGraphQLRequest } from '@/api'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import LoadingSpinner from '@/components/Common/LoadingSpinner'
import { ImagePlus, Trash2, X as XIcon, Pencil, Check } from 'lucide-react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4050'

const GET_AVATARS = gql`
  query GetAvatars($limit: Int, $offset: Int) {
    getAvatars(limit: $limit, offset: $offset) {
      id
      name
      description
      elevenlabsAgentId
      language
      createdAt
    }
  }
`

const CREATE_AVATAR = gql`
  mutation CreateAvatar($input: CreateAvatarInput!) {
    createAvatar(input: $input) {
      id
      name
      description
      elevenlabsAgentId
      language
      createdAt
    }
  }
`

const UPDATE_AVATAR = gql`
  mutation UpdateAvatar($id: Int!, $input: UpdateAvatarInput!) {
    updateAvatar(id: $id, input: $input) {
      id
      name
      description
      elevenlabsAgentId
      language
      createdAt
    }
  }
`

const DELETE_AVATAR = gql`
  mutation DeleteAvatar($id: Int!) {
    deleteAvatar(id: $id) {
      success
      message
    }
  }
`

interface AvatarItem {
  id: number
  name: string
  description: string | null
  elevenlabsAgentId: string | null
  language: string | null
  createdAt: string
}

interface EditForm {
  name: string
  description: string
  elevenlabsAgentId: string
  language: string
}

const AvatarsTab: React.FC = () => {
  const [avatars, setAvatars] = useState<AvatarItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [uploadingId, setUploadingId] = useState<number | null>(null)
  const [imageKeys, setImageKeys] = useState<Record<number, number>>({})
  const [imageError, setImageError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState<EditForm>({ name: '', description: '', elevenlabsAgentId: '', language: '' })
  const [savingId, setSavingId] = useState<number | null>(null)

  const imageInputRef = useRef<HTMLInputElement>(null)
  const imageTargetIdRef = useRef<number | null>(null)

  const [newAvatar, setNewAvatar] = useState({
    name: '',
    description: '',
    elevenlabsAgentId: '',
    language: '',
  })

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token')
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  const fetchAvatars = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await authorizedGraphQLRequest<{ getAvatars: AvatarItem[] }>(GET_AVATARS, { limit: 100, offset: 0 })
      setAvatars(data.getAvatars || [])
    } catch (err) {
      console.error('Failed to fetch avatars:', err)
      setError('Failed to load avatars')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAvatars()
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setNewAvatar((prev) => ({ ...prev, [name]: value }))
  }

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setEditForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleAddAvatar = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setLoading(true)
      setError(null)
      const data = await authorizedGraphQLRequest<{ createAvatar: AvatarItem }>(CREATE_AVATAR, {
        input: {
          name: newAvatar.name,
          description: newAvatar.description || null,
          elevenlabsAgentId: newAvatar.elevenlabsAgentId || null,
          language: newAvatar.language || null,
        },
      })
      setAvatars((prev) => [...prev, data.createAvatar])
      setNewAvatar({ name: '', description: '', elevenlabsAgentId: '', language: '' })
      setShowAddForm(false)
    } catch (err) {
      console.error('Failed to create avatar:', err)
      setError('Failed to create avatar')
    } finally {
      setLoading(false)
    }
  }

  const handleEditStart = (avatar: AvatarItem) => {
    setEditingId(avatar.id)
    setEditForm({
      name: avatar.name,
      description: avatar.description || '',
      elevenlabsAgentId: avatar.elevenlabsAgentId || '',
      language: avatar.language || '',
    })
  }

  const handleEditCancel = () => {
    setEditingId(null)
  }

  const handleEditSave = async (id: number) => {
    try {
      setSavingId(id)
      setError(null)
      const data = await authorizedGraphQLRequest<{ updateAvatar: AvatarItem }>(UPDATE_AVATAR, {
        id,
        input: {
          name: editForm.name,
          description: editForm.description || null,
          elevenlabsAgentId: editForm.elevenlabsAgentId || null,
          language: editForm.language || null,
        },
      })
      setAvatars((prev) => prev.map((a) => (a.id === id ? data.updateAvatar : a)))
      setEditingId(null)
    } catch (err) {
      console.error('Failed to update avatar:', err)
      setError('Failed to update avatar')
    } finally {
      setSavingId(null)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this avatar? Targets using it will have their avatar unassigned.')) return
    try {
      setDeletingId(id)
      await authorizedGraphQLRequest(DELETE_AVATAR, { id })
      setAvatars((prev) => prev.filter((a) => a.id !== id))
    } catch (err) {
      console.error('Failed to delete avatar:', err)
      setError('Failed to delete avatar')
    } finally {
      setDeletingId(null)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const avatarId = imageTargetIdRef.current
    if (!file || !avatarId) return

    setImageError(null)

    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setImageError('Invalid file type. Only PNG and JPEG are accepted.')
      return
    }
    if (file.size > 200 * 1024) {
      setImageError('File too large. Maximum size is 200KB.')
      return
    }

    const formData = new FormData()
    formData.append('image', file)

    try {
      setUploadingId(avatarId)
      await axios.post(`${API_URL}/api/avatars/${avatarId}/image`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      })
      setImageKeys((prev) => ({ ...prev, [avatarId]: (prev[avatarId] || 0) + 1 }))
    } catch (err: any) {
      setImageError(err?.response?.data?.error || 'Failed to upload image.')
    } finally {
      setUploadingId(null)
      if (imageInputRef.current) imageInputRef.current.value = ''
    }
  }

  const handleImageRemove = async (avatarId: number) => {
    if (!confirm('Remove the image for this avatar?')) return
    setImageError(null)
    try {
      setUploadingId(avatarId)
      await axios.delete(`${API_URL}/api/avatars/${avatarId}/image`, { headers: getAuthHeaders() })
      setImageKeys((prev) => ({ ...prev, [avatarId]: (prev[avatarId] || 0) + 1 }))
    } catch (err: any) {
      setImageError(err?.response?.data?.error || 'Failed to remove image.')
    } finally {
      setUploadingId(null)
    }
  }

  return (
    <div className="space-y-6">
      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}

      <div className="flex justify-end">
        <Button onClick={() => setShowAddForm(!showAddForm)} className="bg-primary hover:bg-primary/90">
          {showAddForm ? 'Cancel' : 'Add New Avatar'}
        </Button>
      </div>

      {showAddForm && (
        <Card className="p-4 border border-gray-200 mb-6">
          <h3 className="text-lg font-medium mb-4">Add New Avatar</h3>
          <form onSubmit={handleAddAvatar} className="space-y-4">
            <div>
              <Label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </Label>
              <Input id="name" name="name" value={newAvatar.name} onChange={handleInputChange} required />
            </div>
            <div>
              <Label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </Label>
              <Input id="description" name="description" value={newAvatar.description} onChange={handleInputChange} />
            </div>
            <div>
              <Label htmlFor="elevenlabsAgentId" className="block text-sm font-medium text-gray-700 mb-1">
                ElevenLabs Agent ID
              </Label>
              <Input id="elevenlabsAgentId" name="elevenlabsAgentId" value={newAvatar.elevenlabsAgentId} onChange={handleInputChange} placeholder="agent_..." />
            </div>
            <div>
              <Label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                Language
              </Label>
              <Input id="language" name="language" value={newAvatar.language} onChange={handleInputChange} placeholder="e.g. en, fr, es, de" />
            </div>
            <div className="pt-4 border-t flex justify-end space-x-2">
              <Button type="button" onClick={() => setShowAddForm(false)} variant="outline">
                Cancel
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={loading}>
                {loading ? <LoadingSpinner size="sm" color="white" /> : 'Add Avatar'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {loading && !showAddForm ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" color="teal" />
        </div>
      ) : (
        <>
          {imageError && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg">{imageError}</div>}
          <input ref={imageInputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleImageUpload} />
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Language</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {avatars.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                      No avatars created yet
                    </td>
                  </tr>
                ) : (
                  avatars.map((avatar) => {
                    const isEditing = editingId === avatar.id
                    return (
                      <tr key={avatar.id} className={isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                        {/* Image cell — always read-only */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <div className="w-10 h-10 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
                              <img
                                src={`${API_URL}/api/avatars/${avatar.id}/image?v=${imageKeys[avatar.id] || 0}`}
                                alt=""
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  ;(e.target as HTMLImageElement).style.display = 'none'
                                  ;(e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden')
                                }}
                                onLoad={(e) => {
                                  ;(e.target as HTMLImageElement).style.display = ''
                                  const sib = (e.target as HTMLImageElement).nextElementSibling
                                  if (sib) sib.classList.add('hidden')
                                }}
                              />
                              <ImagePlus className="h-4 w-4 text-gray-400" />
                            </div>
                            <div className="flex flex-col gap-0.5">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-gray-500 hover:text-primary"
                                disabled={uploadingId === avatar.id}
                                onClick={() => {
                                  imageTargetIdRef.current = avatar.id
                                  imageInputRef.current?.click()
                                }}
                              >
                                <ImagePlus className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                                disabled={uploadingId === avatar.id}
                                onClick={() => handleImageRemove(avatar.id)}
                              >
                                <XIcon className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </td>

                        {/* Editable cells */}
                        {isEditing ? (
                          <>
                            <td className="px-3 py-2">
                              <Input name="name" value={editForm.name} onChange={handleEditChange} className="h-8 text-sm" required />
                            </td>
                            <td className="px-3 py-2">
                              <Input name="description" value={editForm.description} onChange={handleEditChange} className="h-8 text-sm" placeholder="—" />
                            </td>
                            <td className="px-3 py-2">
                              <Input name="elevenlabsAgentId" value={editForm.elevenlabsAgentId} onChange={handleEditChange} className="h-8 text-sm font-mono" placeholder="agent_..." />
                            </td>
                            <td className="px-3 py-2">
                              <Input name="language" value={editForm.language} onChange={handleEditChange} className="h-8 text-sm w-20" placeholder="en" />
                            </td>
                            <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">{new Date(avatar.createdAt).toLocaleDateString()}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditSave(avatar.id)}
                                  disabled={savingId === avatar.id}
                                  className="text-green-600 hover:text-green-800 hover:bg-green-50"
                                >
                                  {savingId === avatar.id ? <LoadingSpinner size="sm" color="gray" showText={false} /> : <Check className="h-4 w-4" />}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={handleEditCancel} className="text-gray-500 hover:text-gray-700 hover:bg-gray-100">
                                  <XIcon className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{avatar.name}</td>
                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{avatar.description || <span className="text-gray-300">&mdash;</span>}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {avatar.elevenlabsAgentId ? <span title={avatar.elevenlabsAgentId}>{avatar.elevenlabsAgentId.slice(0, 16)}...</span> : <span className="text-gray-300">&mdash;</span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{avatar.language || <span className="text-gray-300">&mdash;</span>}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(avatar.createdAt).toLocaleDateString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleEditStart(avatar)} className="text-gray-600 hover:text-gray-800 hover:bg-gray-100">
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(avatar.id)}
                                  disabled={deletingId === avatar.id}
                                  className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

export default AvatarsTab
